// react
import { useTranslation } from 'react-i18next';

// types
import { Country, Project, StatsCounts, TraceabilityTransaction } from '../../../../@types';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { ChipTraceability } from '../../../ui/ChipTraceability/ChipTraceability';
import { FlagText } from '../../../ui/FlagText/FlagText';
import { IconCircle } from '../../../ui/IconCircle/IconCircle';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';

// links
import { LinkProject } from '../../../links/LinkProject/LinkProject';

// props
export interface TableBasicProjectsCountsProps extends Omit<TableProps, 'headers'> {
	data: StatsCounts[];
}

const formatDate = (value: string | null): string =>
	value ? new Date(value).toLocaleDateString() : '-';

export const TableBasicProjectsCounts = ({
	data = [],
	...props
}: TableBasicProjectsCountsProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const headers: TableHeader[] = [
		{
			isSortable: true,
			label: t('project'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Grid alignItems="center" container spacing={1} wrap="nowrap">
					<Grid item>
						<IconCircle
							size="xs"
							TooltipProps={{
								componentsProps: { tooltip: { sx: { maxWidth: 160 } } },
								title: row.project,
							}}
							type="project"
						/>
					</Grid>
					<Grid flex={1} item>
						<LinkProject
							children={row.idWbg}
							project={{ idWbg: row.idWbg, name: row.project } as Project}
							sx={{ whiteSpace: 'nowrap' }}
						/>
					</Grid>
				</Grid>
			),
			value: 'idWbg',
		},
		{
			isSortable: true,
			label: t('onboardingDate'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{formatDate(row.onboardingDate)}</Typography>
			),
			value: 'onboardingDate',
		},
		{
			isSortable: true,
			label: t('name'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography sx={{ whiteSpace: 'nowrap' }}>{row.project}</Typography>
			),
			value: 'name',
		},
		{
			label: t('country'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<FlagText country={{ name: row.country } as Country} />
			),
			value: 'country',
		},
		{
			label: t('region'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.region ?? '-'}</Typography>
			),
			value: 'region',
		},
		{
			label: t('blockchainTransactions'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.blockchainTransactions}</Typography>
			),
			value: 'blockchainTransactions',
		},
		{
			label: t('mostRecentTransactionDate'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{formatDate(row.mostRecentTxDate)}</Typography>
			),
			value: 'mostRecentTxDate',
		},
		{
			label: t('transactions'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.transactions}</Typography>
			),
			value: 'transactions',
		},
		{
			label: t('documents'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.documentCount}</Typography>
			),
			value: 'documentCount',
		},
		{
			label: t('fullyEvidencedTransactions'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.fullyEvidenced}</Typography>
			),
			value: 'fullyEvidenced',
		},
		{
			label: t('partiallyEvidencedTransactions'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.partiallyEvidenced}</Typography>
			),
			value: 'partiallyEvidenced',
		},
		{
			label: t('notEvidencedTransactions'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.notEvidenced}</Typography>
			),
			value: 'notEvidenced',
		},
		{
			label: t('paymentsRecorded'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.paymentNotarization}</Typography>
			),
			value: 'paymentNotarization',
		},
		{
			label: t('entities'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.entities}</Typography>
			),
			value: 'entities',
		},
		{
			label: t('bankAccounts'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.accounts}</Typography>
			),
			value: 'accounts',
		},
		{
			label: t('usersPiuSubPiuIntermediary'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.usersPiuSubInter}</Typography>
			),
			value: 'usersPiuSubInter',
		},
		{
			label: t('supplierUsers'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.supplierUsers}</Typography>
			),
			value: 'supplierUsers',
		},
		{
			label: t('suppliers'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.suppliers}</Typography>
			),
			value: 'suppliers',
		},
		{
			label: t('beneficiaries'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<Typography>{row.beneficiaries}</Typography>
			),
			value: 'beneficiaries',
		},
		{
			label: t('transactionTraceabilityPercent'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<ChipTraceability
					traceability={{ value: row.transactionTraceability ?? 0 } as TraceabilityTransaction}
					type="TRANSACTIONS"
				/>
			),
			value: 'transactionTraceability',
		},
		{
			label: t('financialTraceabilityPercent'),
			render: (_: unknown, __: TableHeader, row: StatsCounts) => (
				<ChipTraceability
					traceability={{ value: row.financialTraceability ?? 0 } as TraceabilityTransaction}
					type="FINANCIAL"
				/>
			),
			value: 'financialTraceability',
		},
	];

	return <Table color="grey" {...props} data={data} headers={headers} />;
};
